import type { LinhaDoPedidoAntigo } from "@/hooks/use-cart";
import { getStoreTimezone } from "./store-timezone";

/** Pedido do cliente como `GET /api/catalog/:slug/orders` devolve (o tipo gerado não traz as opções dos itens). */
export interface PedidoDoCliente {
  id: string;
  status: string;
  total: string | number;
  createdAt: string;
  storeTimezone?: string | null;
  items: Array<LinhaDoPedidoAntigo & { id?: string }>;
}

// O status como o cliente entende (o painel fala com o lojista: «Pendente», «Avisou Motoboy»).
const STATUS_PARA_O_CLIENTE: Record<string, string> = {
  novo: "Recebido",
  confirmado: "Confirmado",
  enviar_frete: "Calculando o frete",
  em_preparo: "Em preparo",
  em_separacao: "Em separação",
  pronto: "Pronto",
  aguardando_retirada: "Pronto para retirar",
  avisou_motoboy: "Saindo para entrega",
  enviado: "Saiu para entrega",
  entregue: "Entregue",
  cancelado: "Cancelado",
};

export function statusParaOCliente(status: string): string {
  return STATUS_PARA_O_CLIENTE[status] ?? (status ? status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ") : "—");
}

/** «23/10» no fuso da loja. */
export function diaEMes(iso: string, tz?: string | null): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pt-BR", { timeZone: tz || getStoreTimezone(), day: "2-digit", month: "2-digit" });
}

/** «23/10 às 21:27» no fuso da loja. */
export function diaEHora(iso: string, tz?: string | null): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const fuso = tz || getStoreTimezone();
  return `${diaEMes(iso, fuso)} às ${d.toLocaleTimeString("pt-BR", { timeZone: fuso, hour: "2-digit", minute: "2-digit" })}`;
}

/** «1x Gin Tradicional, 2x Caipirinha». */
export function resumoDosItens(items: PedidoDoCliente["items"]): string {
  return items.map((i) => `${i.quantity}x ${i.productName}`).join(", ");
}

// Da Minha conta para a vitrine: as linhas vão na aba (sessionStorage) e a vitrine, com o catálogo na mão, põe no
// carrinho uma vez só (recarregar a página não põe de novo).
const chave = (slug: string) => `vnz-pedir-de-novo-${slug}`;

export function guardarPedidoDeNovo(slug: string, linhas: LinhaDoPedidoAntigo[]): void {
  try {
    sessionStorage.setItem(chave(slug), JSON.stringify(linhas));
  } catch {}
}

export function tirarPedidoDeNovo(slug: string): LinhaDoPedidoAntigo[] | null {
  try {
    const bruto = sessionStorage.getItem(chave(slug));
    if (!bruto) return null;
    sessionStorage.removeItem(chave(slug));
    const linhas = JSON.parse(bruto);
    return Array.isArray(linhas) ? linhas : null;
  } catch {
    return null;
  }
}
